import type { TProduct } from "./product";

/**
 * Per-product brand identity — the single source of truth for the public name,
 * tagline, marketing description and PWA manifest colours.
 *
 * Historically these strings were re-typed inline in four+ places (the dynamic
 * `/manifest.webmanifest` route, `/api/product`, `lib/og-tags.ts`, and the
 * Astro landing layouts). They drifted: the Voxcanon tagline alone existed in
 * four variants. This module is now the one place each value lives; backend
 * consumers import from here.
 *
 * NOTE: the Astro `landing/` workspace is NOT part of the Bun workspace and can
 * not import `@lexicanon/shared`, so its layouts keep their own copies for now
 * (tracked separately). Everything that CAN import shared does.
 */
export interface IBrandConstants {
	/** Display name, e.g. "Lexicanon". */
	name: string;
	/** Short marketing tagline. */
	tagline: string;
	/** Longer marketing description (used by OG/manifest descriptions). */
	description: string;
	/**
	 * PWA manifest `theme_color`. Mirrors a Layer-1 token (kept in sync with
	 * the comment next to each value) — the manifest is served as static JSON
	 * so it cannot read a CSS var at request time.
	 */
	manifestThemeColor: string;
	/** PWA manifest `background_color` (splash background). */
	manifestBackgroundColor: string;
}

export const BRAND_CONSTANTS: Record<TProduct, IBrandConstants> = {
	lexicanon: {
		name: "Lexicanon",
		tagline: "Meeting intelligence for every room.",
		description: "Meeting intelligence — transcription, analysis, and insights.",
		// Mirrors --lex-pc-n-0 (Pastel Calm light body — the product default
		// since 2026-05-30); a dark splash here flashed near-black chrome before
		// the pastel app painted.
		manifestThemeColor: "#f4f2fb",
		manifestBackgroundColor: "#f4f2fb",
	},
	voxcanon: {
		name: "Voxcanon",
		tagline: "Every Session Remembered. Every Story, Canon.",
		description: "Every Session Remembered. Every Story, Canon.",
		// Mirrors --vox-arcane (the brand violet).
		manifestThemeColor: "#6d28d9",
		manifestBackgroundColor: "#0d0019",
	},
};
